import useDisabledAccount from '../hooks/account/UseDisabledAccount';
import { useAccount } from '../contexts/account/AccountContexts';
import { Navigate, useLocation } from 'react-router-dom';
import { useAccountSubscriptionSocket } from '../hooks/socket/UseAccountSubscriptionSocket';
import {
  ADS,
  BOT_ACCESS,
  CHANGE_PASSWORD,
  CONTEXT_ANALYSIS,
  FAQ,
  LOGIN,
  MY_ACCOUNT,
  PRICING,
  PROJECTS,
  QUEST_FAQ,
  SETTINGS,
  SOURCES,
  TRIAL_ENDED,
  YOUR_ACCOUNTS,
} from './path';
import Layout from '../components/common/layout/Layout';
import TrialEndedPage from '../../pages/trial-ended/TrialEndedPage';
import Wrapper from '../components/common/wrapper/Wrapper';
import PricingPage from '../../pages/pricing/PricingPage';
import BannedPage from '../../pages/banned/BannedPage';
import { useAuth } from '../contexts/auth/AuthContext';
import { SocketProvider } from '../contexts/socket/SocketContext';
import { ensureAttributionCookieFromRoute } from '../services/account-attribution/AccountAttributionService';

/**
 * Adresy, pod które `GUEST` może wejść. Odpowiednik whitelisty menu (`buildProjectMenu` +  pozycja
 * FAQ w `Panel`) — menu decyduje, czego gość NIE WIDZI, ta lista decyduje, czego nie OTWORZY z ręki.
 *
 * Powód: ukrycie pozycji w menu było dotąd jedyną barierą, a za realną obronę robiła bramka
 * `allowGuest` na backendzie. Działa to wszędzie tam, gdzie backend jest zamknięty, ale nie na
 * stronach, których odczyt MUSI być otwarty: `/tags` i `/prompts` czytają te same endpointy co
 * dashboard i strona promptu, więc gość wklejający adres dostawał je w całości.
 *
 * Trzymane jako prefiksy, bo trasy projektowe niosą `:projectId/:period/:chat`. Podstrony `/projects`
 * z zapisem (kreator projektu, dodawanie promptu) pilnują się same przez `usePermission('PROJECTS')`.
 */
const GUEST_ALLOWED_PATHS = [
  PROJECTS,
  SOURCES,
  CONTEXT_ANALYSIS,
  ADS,
  BOT_ACCESS,
  FAQ,
  MY_ACCOUNT,
  CHANGE_PASSWORD,
  YOUR_ACCOUNTS,
  '/invite',
];

const isGuestAllowedPath = (pathname: string): boolean =>
  GUEST_ALLOWED_PATHS.some((allowed) => pathname === allowed || pathname.startsWith(`${allowed}/`));

const PrivateRoutesWithSocket = ({ children }: React.PropsWithChildren<{}>) => {
  const { isDisabled } = useDisabledAccount();
  const { accountResponse } = useAccount();
  const location = useLocation();

  useAccountSubscriptionSocket();

  const isSettingsAddress = location.pathname.startsWith(SETTINGS);
  const isPricing = location.pathname.startsWith(PRICING);
  const isFAQ = location.pathname.startsWith(FAQ);
  const isTrialEnded = location.pathname.startsWith(TRIAL_ENDED);

  // Before every other gate: a banned account gets this screen on any address, with no Layout (no
  // menu). The backend 403s everything but `/account/data`, which Wrapper fetches to render this.
  if (accountResponse?.isBanned) {
    return (
      <Wrapper>
        <BannedPage />
      </Wrapper>
    );
  }

  if (isDisabled && !isSettingsAddress && !isPricing && !isFAQ && !isTrialEnded) {
    if (accountResponse?.accountType === 'TRIAL') {
      window.history.replaceState(null, '', TRIAL_ENDED);
      return (
        <Wrapper canShowPricing={true}>
          <Layout dataPage="trial-ended">
            <TrialEndedPage />
          </Layout>
        </Wrapper>
      );
    } else {
      window.history.replaceState(null, '', PRICING);
      return (
        <Wrapper canShowPricing={true}>
          <Layout>
            <PricingPage />
          </Layout>
        </Wrapper>
      );
    }
  }

  // Po bloku `isDisabled`, żeby nie zmieniać tego, co widzi gość na zablokowanym koncie.
  if (accountResponse?.role === 'GUEST' && !isGuestAllowedPath(location.pathname)) {
    return <Navigate to={PROJECTS} replace />;
  }

  return <>{children}</>;
};

const PrivateRoutes = ({ children }: React.PropsWithChildren<{}>) => {
  const { isAuthenticated } = useAuth();
  const { accountResponse } = useAccount();
  const location = useLocation();

  if (!isAuthenticated) {
    ensureAttributionCookieFromRoute({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });

    if (location.pathname === FAQ) {
      return <Navigate to={QUEST_FAQ} />;
    }

    return <Navigate to={LOGIN} state={{ from: location }} replace />;
  }

  return (
    <SocketProvider accountId={accountResponse?.accountId ?? null}>
      <PrivateRoutesWithSocket>{children}</PrivateRoutesWithSocket>
    </SocketProvider>
  );
};

export default PrivateRoutes;
